import { defineStore } from 'pinia'
import { resetViewFilters } from '../composables/viewFilters'
import { useTenantStore, type TenantOption } from './tenant'

export const AUTHENTICATION_REQUIRED_EVENT = 'ndconml:authentication-required'

export interface AuthSession {
  authenticated: boolean
  username: string | null
  displayName: string | null
  source: 'LOCAL' | 'LDAP' | 'LDAP_GLOBAL' | null
  profileEditAllowed: boolean
  authorities: string[]
  csrfToken: string
  passwordChangeAllowed: boolean
  activeTenantId: string | null
  tenants: TenantOption[]
  tenantFilterAllowed: boolean
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    initialized: false,
    authenticated: false,
    username: null as string | null,
    displayName: null as string | null,
    source: null as 'LOCAL' | 'LDAP' | 'LDAP_GLOBAL' | null,
    profileEditAllowed: false,
    authorities: [] as string[],
    csrfToken: '',
    passwordChangeAllowed: false,
    tenantFilterAllowed: false,
    loading: false,
    error: ''
  }),
  actions: {
    apply(session: AuthSession) {
      if (this.authenticated !== session.authenticated || this.username !== session.username) resetViewFilters()
      this.initialized = true
      this.authenticated = session.authenticated
      this.username = session.username
      this.displayName = session.displayName ?? session.username
      this.source = session.source ?? null
      this.profileEditAllowed = Boolean(session.profileEditAllowed)
      this.authorities = session.authorities
      this.csrfToken = session.csrfToken
      this.passwordChangeAllowed = Boolean(session.passwordChangeAllowed)
      this.tenantFilterAllowed = Boolean(session.tenantFilterAllowed)
      if (session.authenticated) useTenantStore().synchronize(session.tenants ?? [], session.activeTenantId)
    },
    expire() {
      resetViewFilters()
      this.initialized = true
      this.authenticated = false
      this.username = null
      this.displayName = null
      this.source = null
      this.profileEditAllowed = false
      this.authorities = []
      this.csrfToken = ''
      this.passwordChangeAllowed = false
      this.tenantFilterAllowed = false
    },
    async refresh() {
      this.loading = true
      try {
        const response = await fetch('/api/auth/session', {
          credentials: 'same-origin', headers: { Accept: 'application/json',
            'X-NDConML-Tenant': useTenantStore().tenantId }
        })
        if (!response.ok) throw new Error(`Authentication service returned ${response.status}`)
        this.apply(await response.json() as AuthSession)
      } catch (error) {
        this.expire()
        this.error = error instanceof Error ? error.message : String(error)
      } finally {
        this.loading = false
      }
      return this.authenticated
    },
    async login(username: string, password: string) {
      resetViewFilters()
      this.loading = true
      this.error = ''
      try {
        if (!this.csrfToken) await this.refresh()
        const body = new URLSearchParams({ username, password })
        const response = await fetch('/api/auth/login', {
          method: 'POST', credentials: 'same-origin', body,
          headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'X-XSRF-TOKEN': this.csrfToken,
            'X-NDConML-Tenant': useTenantStore().tenantId }
        })
        if (!response.ok) throw new Error(response.status === 401
          ? 'Nesprávne používateľské meno alebo heslo.' : `Prihlásenie zlyhalo (${response.status}).`)
        await this.refresh()
        if (!this.authenticated) throw new Error('Server nevytvoril prihlásenú session.')
      } catch (error) {
        this.expire()
        this.error = error instanceof Error ? error.message : String(error)
        throw error
      } finally {
        this.loading = false
      }
    },
    async logout() {
      try {
        await fetch('/api/auth/logout', {
          method: 'POST', credentials: 'same-origin', headers: { 'X-XSRF-TOKEN': this.csrfToken }
        })
      } finally {
        this.expire()
      }
    }
  }
})
